import { NoticeCategory } from '@core/databus/IMessage'
import type { ActionNoticeData, AsyncOperationEventData, TransmissionNoticeData } from '@core/databus/DataSyncManager'
import type { EventCallbackAggregator, EventCallbackGroup } from '@core/databus/DataSyncEventChangeListener'
import type { NoticeEventChangeCallback } from '@core/databus/IDataSyncManager'

export abstract class NoticeEventDispatcher {
  readonly #actionNoticeList: ActionNoticeData[] = []
  readonly #transmissionNoticeList: TransmissionNoticeData[] = []
  readonly #asyncOperationEventList: AsyncOperationEventData[] = []

  protected _callbackGetter: EventCallbackAggregator

  constructor(callbackGetter: EventCallbackAggregator) {
    this._callbackGetter = callbackGetter
  }

  append<T extends ActionNoticeData | TransmissionNoticeData | AsyncOperationEventData>(notice: T) {
    switch (notice.category) {
      case NoticeCategory.NOTICE:
        this.#actionNoticeList.push(<ActionNoticeData>notice)
        break
      case NoticeCategory.TRANSMISSION:
        this.#transmissionNoticeList.push(<TransmissionNoticeData>notice)
        break
      case NoticeCategory.EVENT:
        this.#asyncOperationEventList.push(<AsyncOperationEventData>notice)
        break

      default:
        break
    }
  }

  notifyChanges() {
    const callbackGroup = this.#getCallbacks()
    const { actionNotice, asyncOperationEvent, transmissionNotice } = callbackGroup

    console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, '加入的事件集合', callbackGroup)

    for (const callback of actionNotice) {
      callback(this.#actionNoticeList)
    }

    for (const callback of asyncOperationEvent) {
      callback(this.#asyncOperationEventList)
    }

    for (const callback of transmissionNotice) {
      callback(this.#transmissionNoticeList)
    }
  }

  /**
   * 返回可调用的callback functions
   * @protected
   */
  protected abstract _getCallbacksImpl(): EventCallbackGroup

  #getCallbacks(): EventCallbackGroup {
    const group = this._getCallbacksImpl()

    function filterCallbacks<T extends ActionNoticeData | TransmissionNoticeData | AsyncOperationEventData>(
      callbacks: Array<NoticeEventChangeCallback<T>>,
      eventList: Array<T>
    ) {
      return eventList.length > 0 ? callbacks : []
    }

    group.actionNotice = filterCallbacks(group.actionNotice, this.#actionNoticeList)
    group.transmissionNotice = filterCallbacks(group.transmissionNotice, this.#transmissionNoticeList)
    group.asyncOperationEvent = filterCallbacks(group.asyncOperationEvent, this.#asyncOperationEventList)

    return group
  }
}

export class NoticeAddedEventDispatcher extends NoticeEventDispatcher {
  protected _getCallbacksImpl(): EventCallbackGroup {
    return this._callbackGetter.getAllOnAddedCallbacks()
  }
}

export class NoticeUpdatedEventDispatcher extends NoticeEventDispatcher {
  protected _getCallbacksImpl(): EventCallbackGroup {
    return this._callbackGetter.getAllOnUpdatedCallbacks()
  }
}

export class NoticeRemovedEventDispatcher extends NoticeEventDispatcher {
  protected _getCallbacksImpl(): EventCallbackGroup {
    return this._callbackGetter.getAllOnRemovedCallbacks()
  }
}
